import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const LeaveHistory = () => {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    status: '',
    leaveType: ''
  });
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [leaveToCancel, setLeaveToCancel] = useState(null);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [selectedEmployeeName, setSelectedEmployeeName] = useState('');
  
  // Check if user is a reporting manager
  const isReportingManager = localStorage.getItem('isReportingManager') === 'true';

  useEffect(() => {
    fetchLeaveHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, filters]);

  const fetchLeaveHistory = async () => {
    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    setLoading(true);
    try {
      const employeeId = localStorage.getItem('id');
      
      if (!employeeId) {
        toast.error('User ID not found. Please login again.');
        return;
      }

      const payload = {
        action: 'get-leave-history',
        employeeId: employeeId,
        page: pagination.currentPage,
        limit: pagination.limit,
        ...(filters.year && { year: filters.year }),
        ...(filters.status && { status: filters.status }),
        ...(filters.leaveType && { leaveType: filters.leaveType })
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        setLeaves(result.data || []);
        setPagination(prev => ({
          ...prev,
          currentPage: result.page || prev.currentPage,
          totalPages: result.totalPages || prev.totalPages,
          totalCount: result.total || prev.totalCount,
          hasNextPage: (result.page || prev.currentPage) < (result.totalPages || prev.totalPages),
          hasPreviousPage: (result.page || prev.currentPage) > 1
        }));
      } else {
        toast.error(result?.message || 'Failed to fetch leave history');
      }
    } catch (error) {
      console.error('Error fetching leave history:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const day = date.getDate().toString().padStart(2, '0');
        const month = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      }
      return dateString;
    } catch (error) {
      return dateString;
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const getPaginationItems = () => {
    const totalPages = pagination.totalPages;
    const currentPage = pagination.currentPage;

    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const items = [];
    const addPage = (page) => {
      if (!items.includes(page)) {
        items.push(page);
      }
    };
    const addEllipsis = () => {
      if (items[items.length - 1] !== 'ellipsis') {
        items.push('ellipsis');
      }
    };

    if (currentPage <= 3) {
      for (let page = 1; page <= Math.min(5, totalPages); page += 1) {
        addPage(page);
      }
      if (totalPages > 8) {
        addEllipsis();
      }
      for (let page = Math.max(totalPages - 2, 1); page <= totalPages; page += 1) {
        addPage(page);
      }
    } else if (currentPage >= totalPages - 2) {
      for (let page = 1; page <= Math.min(3, totalPages); page += 1) {
        addPage(page);
      }
      if (totalPages > 8) {
        addEllipsis();
      }
      for (let page = Math.max(totalPages - 4, 1); page <= totalPages; page += 1) {
        addPage(page);
      }
    } else {
      for (let page = Math.max(currentPage - 2, 1); page <= Math.min(currentPage + 2, totalPages); page += 1) {
        addPage(page);
      }
      addEllipsis();
      for (let page = Math.max(totalPages - 3, 1); page <= totalPages; page += 1) {
        addPage(page);
      }
    }

    return items;
  };

  const getSerialNumber = (index) => {
    return (pagination.currentPage - 1) * pagination.limit + index + 1;
  };

  const handleCancel = (leave) => {
    setLeaveToCancel(leave);
    setShowCancelModal(true);
  };

  const closeCancelModal = () => {
    setShowCancelModal(false);
    setLeaveToCancel(null);
  };

  const confirmCancel = async () => {
    if (!leaveToCancel) return;

    const employeeId = localStorage.getItem('id');
    
    if (!employeeId) {
      toast.error('User ID not found. Please login again.');
      closeCancelModal();
      return;
    }

    setLoading(true);
    try {
      const payload = {
        action: 'cancel-leave',
        leaveId: leaveToCancel.id,
        employeeId: employeeId
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        toast.success(result?.message || 'Leave cancelled successfully');
        closeCancelModal();
        fetchLeaveHistory();
      } else {
        toast.error(result?.message || 'Failed to cancel leave');
      }
    } catch (error) {
      console.error('Error cancelling leave:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase();
    let className = 'employee-status';
    let text = status?.toUpperCase() || 'PENDING';

    switch (statusLower) {
      case 'approved':
        className += ' employee-status-active';
        break;
      case 'rejected':
        className += ' employee-status-inactive';
        break;
      case 'cancelled':
        className += ' employee-status-inactive';
        break;
      default:
        className += ' employee-status-active';
        text = 'PENDING';
    }

    return <span className={className}>{text}</span>;
  };

  const getLeaveTypeLabel = (type) => {
    const types = {
      'casual': 'Casual',
      'sick': 'Sick',
      'earned': 'Earned',
      'lop': 'LOP',
      'permission': 'Permission',
      'Travel': 'Travel'
    };
    return types[type] || type?.toUpperCase() || '-';
  };

  const truncateText = (text, maxLength = 30) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getEmployeeDisplayName = () => {
    return localStorage.getItem('name') || 'Employee';
  };

  const handleViewReason = (reason, employeeName) => {
    setSelectedReason(reason || '-');
    setSelectedEmployeeName(employeeName || getEmployeeDisplayName());
    setShowReasonModal(true);
  };

  const closeReasonModal = () => {
    setShowReasonModal(false);
    setSelectedReason('');
    setSelectedEmployeeName('');
  };

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">
          {/* Header with Title */}
          <div className="row mb-3 employee-header-row">
            <div className="col-md-12">
              <h1 className="employee-title">
                My Leave History
                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
              </h1>
              <p className="employee-description">
                View your leave application history and status.
              </p>
            </div>
          </div>

          {/* Leave History Table Card */}
          <div className="card employee-content-card">
            <div className="card-body">
              <div className="employee-table-container">
                {/* Filters */}
                <div className="employee-search-container" style={{ marginBottom: '20px' }}>
                  <div className="row">
                    <div className="col-md-3">
                      <select
                        className="form-control"
                        value={filters.status}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                        style={{ padding: '6px 10px', fontSize: '14px', height: '36px' }}
                      >
                        <option value="">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <select
                        className="form-control"
                        value={filters.leaveType}
                        onChange={(e) => handleFilterChange('leaveType', e.target.value)}
                        style={{ padding: '6px 10px', fontSize: '14px', height: '36px' }}
                      >
                        <option value="">All Leave Types</option>
                        <option value="casual">Casual</option>
                        <option value="sick">Sick</option>
                        <option value="earned">Earned</option>
                        <option value="lop">LOP</option>
                        <option value="permission">Permission</option>
                        <option value="Travel">Travel</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Year"
                        value={filters.year}
                        onChange={(e) => handleFilterChange('year', e.target.value)}
                        style={{ padding: '6px 10px', fontSize: '14px', height: '36px' }}
                      />
                    </div>
                  </div>
                </div>

                {loading ? (
                  <Loading size={120} />
                ) : (
                  <>
                    <div className="employee-table-wrapper">
                      <table className="employee-table">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Leave Type</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Days</th>
                            <th>Status</th>
                            <th>Reason</th>
                            {isReportingManager && <th>Action</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {leaves.length === 0 ? (
                            <tr>
                              <td colSpan={isReportingManager ? "8" : "7"} className="employee-empty">
                                No leave history found
                              </td>
                            </tr>
                          ) : (
                            leaves.map((leave, index) => {
                              return (
                                <tr key={leave.id}>
                                  <td>{getSerialNumber(index)}</td>
                                  <td>{getLeaveTypeLabel(leave.leaveType)}</td>
                                  <td>{formatDate(leave.startDate)}</td>
                                  <td>{formatDate(leave.endDate)}</td>
                                  <td>{leave.numberOfDays || '-'}</td>
                                  <td>{getStatusBadge(leave.status)}</td>
                                  <td style={{ maxWidth: '200px' }}>
                                    {leave.reason && leave.reason.length > 30 ? (
                                      <button
                                        type="button"
                                        className="employee-action-btn employee-action-view"
                                        onClick={() => handleViewReason(leave.reason, leave.employeeName)}
                                        title="View full reason"
                                        style={{ 
                                          padding: '2px 6px',
                                          minWidth: 'auto',
                                          fontSize: '12px'
                                        }}
                                      >
                                        <i className="fa-solid fa-eye"></i>
                                      </button>
                                    ) : (
                                      <span>{leave.reason || '-'}</span>
                                    )}
                                  </td>
                                  {isReportingManager && (
                                    <td>
                                      {leave.status === 'pending' && (
                                        <button
                                          className="employee-action-btn employee-action-delete"
                                          onClick={() => handleCancel(leave)}
                                          title="Cancel"
                                        >
                                          <i className="fa-solid fa-times"></i>
                                        </button>
                                      )}
                                      {leave.status !== 'pending' && (
                                        <span style={{ color: '#6b7280', fontSize: '12px' }}>-</span>
                                      )}
                                    </td>
                                  )}
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>

                    {leaves.length > 0 && pagination.totalPages > 0 && (
                      <div className="employee-pagination">
                        <button
                          className="pagination-btn"
                          onClick={() => handlePageChange(1)}
                          disabled={pagination.currentPage === 1}
                        >
                          «
                        </button>
                        <button
                          className="pagination-btn"
                          onClick={() => handlePageChange(pagination.currentPage - 1)}
                          disabled={pagination.currentPage === 1}
                        >
                          ‹
                        </button>
                        {getPaginationItems().map((item, index) => {
                          if (item === 'ellipsis') {
                            return (
                              <span
                                key={`ellipsis-${index}`}
                                className="pagination-btn"
                                style={{
                                  cursor: 'default',
                                  padding: '6px 10px',
                                  backgroundColor: 'transparent',
                                  borderColor: 'transparent'
                                }}
                              >
                                ...
                              </span>
                            );
                          }

                          return (
                            <button
                              key={item}
                              className={`pagination-btn ${pagination.currentPage === item ? 'active' : ''}`}
                              onClick={() => handlePageChange(item)}
                            >
                              {item}
                            </button>
                          );
                        })}
                        <button
                          className="pagination-btn"
                          onClick={() => handlePageChange(pagination.currentPage + 1)}
                          disabled={pagination.currentPage === pagination.totalPages}
                        >
                          ›
                        </button>
                        <button
                          className="pagination-btn"
                          onClick={() => handlePageChange(pagination.totalPages)}
                          disabled={pagination.currentPage === pagination.totalPages}
                        >
                          »
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="modal-overlay" onClick={closeCancelModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Cancel Leave</h3>
              <button className="modal-close" onClick={closeCancelModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to cancel this leave application?</p>
              <p style={{ marginTop: '10px', fontSize: '14px', color: '#6b7280' }}>
                <strong>Leave Type:</strong> {getLeaveTypeLabel(leaveToCancel?.leaveType)}<br />
                <strong>Duration:</strong> {leaveToCancel?.numberOfDays} day(s)<br />
                <strong>From:</strong> {formatDate(leaveToCancel?.startDate)} <strong>To:</strong> {formatDate(leaveToCancel?.endDate)}
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeCancelModal}
              >
                No
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmCancel}
                disabled={loading}
              >
                {loading ? 'Cancelling...' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Reason Modal */}
      {showReasonModal && (
        <div className="modal-overlay" onClick={closeReasonModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Leave Reason</h3>
              <button className="modal-close" onClick={closeReasonModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '10px', fontSize: '14px', color: '#6b7280' }}>
                <strong>Employee:</strong> {selectedEmployeeName}
              </p>
              <div style={{ 
                padding: '12px', 
                backgroundColor: '#f9fafb', 
                borderRadius: '6px',
                border: '1px solid #e5e7eb',
                minHeight: '60px',
                maxHeight: '300px',
                overflowY: 'auto',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                fontSize: '14px',
                lineHeight: '1.6',
                color: '#374151'
              }}>
                {selectedReason}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeReasonModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveHistory;

